import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Search, 
  Mail, 
  Phone, 
  MapPin, 
  Users, 
  X, 
  Filter,
  Check,
  Plus,
  Briefcase,
  Grid3X3,
  List
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useGetCandidatesNotInList } from "@/hooks/lists/useListDetail";
import { normalizeSkill } from "@/utils/skillNormalization";
import { matchesCandidateSearch } from "@/utils/searchUtils";
import { useCandidateStatus, CANDIDATE_STATUS_VALUES } from "@/utils/candidate/statusUtils";

interface AddCandidatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCandidates: (candidateIds: string[]) => Promise<void>;
  listId: string;
  isLoading: boolean;
}

export const AddCandidatesModal = ({
  isOpen,
  onClose,
  onAddCandidates,
  listId,
  isLoading
}: AddCandidatesModalProps) => {
  const { t } = useTranslation();
  const { getStatusConfig, getLocalizedStatus } = useCandidateStatus();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [skillsFilter, setSkillsFilter] = useState<string[]>([]);
  const [skillsOperator, setSkillsOperator] = useState<"AND" | "OR">("AND");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { data: candidates = [], isLoading: isLoadingCandidates, error: candidatesError } = useGetCandidatesNotInList(listId);

  // Debug logging
  useEffect(() => {
    console.log('AddCandidatesModal - Candidates data:', candidates);
    console.log('AddCandidatesModal - Loading:', isLoadingCandidates);
    console.log('AddCandidatesModal - Error:', candidatesError);
  }, [candidates, isLoadingCandidates, candidatesError]);

  // Get all unique skills and statuses from candidates
  const allSkills = useMemo(() => {
    const skills = new Set<string>();
    candidates.forEach(candidate => {
      candidate.skills?.forEach(skill => skills.add(skill));
    });
    return Array.from(skills).sort();
  }, [candidates]);

  const allStatuses = useMemo(() => {
    const availableStatuses = new Set(
      candidates
        .map(candidate => candidate.status?.toLowerCase())
        .filter((status): status is string => Boolean(status))
    );

    return Object.values(CANDIDATE_STATUS_VALUES).filter(status => availableStatuses.has(status));
  }, [candidates]);

  // Filter candidates based on search term, skills, and status
  const filteredCandidates = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerStatusFilter = statusFilter.map(s => s.toLowerCase());
    const normalizedSkillsFilter = skillsFilter.map(skill => normalizeSkill(skill));
    
    return candidates.filter(candidate => {
      // Search term filter - universal search with word-order independence
      const matchesSearch = matchesCandidateSearch(candidate, searchTerm);
      
      // Skills filter
      const matchesSkills = 
        normalizedSkillsFilter.length === 0 || 
        (candidate.skills && 
          (skillsOperator === "AND" 
            ? normalizedSkillsFilter.every(filterSkill => 
                candidate.skills.some(candidateSkill => 
                  normalizeSkill(candidateSkill) === filterSkill
                )
              )
            : normalizedSkillsFilter.some(filterSkill => 
                candidate.skills.some(candidateSkill => 
                  normalizeSkill(candidateSkill) === filterSkill
                )
              )
          )
        );
      
      // Status filter
      const matchesStatus = 
        statusFilter.length === 0 || 
        (candidate.status && lowerStatusFilter.includes(candidate.status.toLowerCase()));
      
      return matchesSearch && matchesSkills && matchesStatus;
    });
  }, [candidates, searchTerm, skillsFilter, skillsOperator, statusFilter]);

  // Filter skills based on search term
  const filteredSkills = allSkills.filter(skill => 
    skill.toLowerCase().includes(skillsSearchTerm.toLowerCase())
  );

  const handleCandidateToggle = (candidateId: string) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map(c => c.id));
    }
  };

  const toggleSkill = (skill: string) => {
    if (skillsFilter.includes(skill)) {
      setSkillsFilter(skillsFilter.filter(s => s !== skill));
    } else {
      setSkillsFilter([...skillsFilter, skill]);
    }
  };

  const toggleStatus = (status: string) => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSkillsFilter([]);
    setSkillsOperator("AND");
    setStatusFilter([]);
    setSkillsSearchTerm("");
  };

  const handleAddCandidates = async () => {
    if (selectedCandidates.length === 0) return;
    
    await onAddCandidates(selectedCandidates);
    setSelectedCandidates([]);
    setSearchTerm("");
    setSkillsFilter([]);
    setStatusFilter([]);
  };

  const handleClose = () => {
    setSelectedCandidates([]);
    setSearchTerm("");
    setSkillsFilter([]);
    setStatusFilter([]);
    setSkillsSearchTerm("");
    onClose();
  };

  const hasActiveFilters = searchTerm || skillsFilter.length > 0 || statusFilter.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[95vh] flex flex-col [&>button]:hidden">
        <DialogHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                {t("lists.addCandidates.title", "Add Candidates to List")}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                {t("lists.addCandidates.description", "Search and select candidates to add to this list")}
              </p>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <X className="h-6 w-6" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-hidden">
          {/* Search and Filters */}
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative px-4 py-2">
              <Search className="absolute left-7 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t("lists.addCandidates.searchPlaceholder", "Search candidates by name, email, or function...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 h-10"
              />
              {searchTerm && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <div className="flex flex-wrap gap-2 items-center">
                {/* Skills Filter */}
                <DropdownMenu open={skillsMenuOpen} onOpenChange={setSkillsMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      <span>{t("candidates.filters.skillsButton", "Skills")}</span>
                      {skillsFilter.length > 0 && (
                        <span className="ml-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-sm">
                          {skillsFilter.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    className="w-64 p-3 bg-white" 
                    align="start"
                  >
                    <div className="space-y-3">
                      {/* Operator Toggle */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{t("candidates.filters.operator.match", "Match")}</span>
                        <ToggleGroup 
                          type="single" 
                          value={skillsOperator} 
                          onValueChange={(value) => value && setSkillsOperator(value as "AND" | "OR")}
                          className="h-8 bg-gray-50 rounded-sm p-1"
                        >
                          <ToggleGroupItem 
                            value="AND" 
                            className="text-xs px-3 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            {t("candidates.filters.operator.all", "All")}
                          </ToggleGroupItem>
                          <ToggleGroupItem 
                            value="OR" 
                            className="text-xs px-3 data-[state=on]:bg-blue-100 data-[state=on]:text-blue-500"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {t("candidates.filters.operator.any", "Any")}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                      
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder={t("candidates.filters.skillsSearchPlaceholder", "Search skills...")}
                          value={skillsSearchTerm}
                          onChange={(e) => setSkillsSearchTerm(e.target.value)}
                          className="pl-8 text-sm h-8"
                        />
                      </div>
                      
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {filteredSkills.length === 0 ? (
                          <p className="text-sm text-gray-500 py-1">{t("candidates.filters.noSkillsMatch", "No skills match")}</p>
                        ) : (
                          filteredSkills.map(skill => (
                            <div key={skill} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`skill-${skill}`} 
                                checked={skillsFilter.includes(skill)}
                                onCheckedChange={() => toggleSkill(skill)}
                              />
                              <Label 
                                htmlFor={`skill-${skill}`}
                                className="text-sm font-normal cursor-pointer flex-1"
                              >
                                {skill}
                              </Label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Status Filter */}
                <DropdownMenu open={statusMenuOpen} onOpenChange={setStatusMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-2"
                    >
                      <Filter className="h-4 w-4" />
                      <span>{t("candidates.filters.statusButton", "Status")}</span>
                      {statusFilter.length > 0 && (
                        <span className="ml-1 bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-sm">
                          {statusFilter.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    className="w-56 p-3 bg-white" 
                    align="start"
                  >
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {allStatuses.map(status => (
                        <div key={status} className="flex items-center space-x-2">
                          <Checkbox 
                            id={`status-${status}`} 
                            checked={statusFilter.includes(status)}
                            onCheckedChange={() => toggleStatus(status)}
                          />
                          <Label 
                            htmlFor={`status-${status}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {getLocalizedStatus(status)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t("candidates.filters.clearAll", "Clear all")}
                  </Button>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex items-center border rounded-sm bg-white">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-r-none border-0"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none border-0"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Results Count and Select All */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {hasActiveFilters ? (
                  <span>
                    {t("lists.addCandidates.filteredResults", "{{count}} candidates found", { count: filteredCandidates.length })}
                  </span>
                ) : (
                  <span>
                    {t("lists.addCandidates.totalResults", "{{count}} candidates available", { count: candidates.length })}
                  </span>
                )}
              </div>
              {filteredCandidates.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <span className="text-sm text-gray-600">
                    {t("lists.addCandidates.selectAll", "Select All")} ({filteredCandidates.length})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Candidates List */}
          <div className="flex-1 overflow-y-auto border rounded-sm bg-gray-50 min-h-0">
            {candidatesError ? (
              <div className="p-8 text-center">
                <div className="text-red-500 mb-4">
                  <X className="h-16 w-16 mx-auto mb-4" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-red-600">
                  Error loading candidates
                </h3>
                <p className="text-gray-500">
                  {candidatesError.message || "Failed to load candidates. Please try again."}
                </p>
              </div>
            ) : isLoadingCandidates ? (
              <div className="p-8 text-center">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">{t("common.loading", "Loading candidates...")}</p>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {hasActiveFilters 
                    ? t("lists.addCandidates.noResults", "No candidates found")
                    : t("lists.addCandidates.noCandidates", "No candidates available")
                  }
                </h3>
                <p className="text-gray-500">
                  {hasActiveFilters 
                    ? t("lists.addCandidates.noResultsDesc", "Try adjusting your search terms or filters")
                    : t("lists.addCandidates.noCandidatesDesc", "All candidates are already in this list")
                  }
                </p>
              </div>
                          ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {filteredCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="bg-white rounded-sm border p-4 hover:shadow-md transition-shadow relative"
                    >
                      <div className="absolute top-3 right-3">
                        <Checkbox
                          checked={selectedCandidates.includes(candidate.id)}
                          onCheckedChange={() => handleCandidateToggle(candidate.id)}
                        />
                      </div>
                      
                      <div className="flex items-start space-x-3 mb-3">
                        <Avatar className="h-12 w-12 flex-shrink-0">
                          <AvatarFallback className="bg-blue-100 text-blue-500">
                            {candidate.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base font-semibold text-gray-900 truncate mb-1">
                            {candidate.name}
                          </h4>
                          <div className="flex items-center gap-2 flex-wrap">
                            {candidate.status && (
                              <Badge 
                                variant="outline"
                                className={`text-xs ${getStatusConfig(candidate.status).borderColor} ${getStatusConfig(candidate.status).color} ${getStatusConfig(candidate.status).bgColor}`}
                              >
                                {getLocalizedStatus(candidate.status)}
                              </Badge>
                            )}
                            {candidate.function && (
                              <Badge variant="secondary" className="text-xs">
                                <Briefcase className="h-3 w-3 mr-1" />
                                {candidate.function}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm text-gray-500">
                        {candidate.email && (
                          <div className="flex items-center">
                            <Mail className="h-3 w-3 mr-2 flex-shrink-0" />
                            <span className="truncate">{candidate.email}</span>
                          </div>
                        )}
                        {candidate.phone && (
                          <div className="flex items-center">
                            <Phone className="h-3 w-3 mr-2 flex-shrink-0" />
                            <span>{candidate.phone}</span>
                          </div>
                        )}
                        {candidate.location && (
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 mr-2 flex-shrink-0" />
                            <span className="truncate">{candidate.location}</span>
                          </div>
                        )}
                      </div>

                      {candidate.skills && candidate.skills.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="flex flex-wrap gap-1">
                            {candidate.skills.slice(0, 3).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y bg-white">
                  {filteredCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="flex items-center space-x-4 p-6 hover:bg-gray-50 transition-colors"
                    >
                      <Checkbox
                        checked={selectedCandidates.includes(candidate.id)}
                        onCheckedChange={() => handleCandidateToggle(candidate.id)}
                      />
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-blue-100 text-blue-500">
                          {candidate.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-base font-semibold text-gray-900 truncate">
                            {candidate.name}
                          </h4>
                          <div className="flex items-center gap-2">
                            {candidate.status && (
                              <Badge 
                                variant="outline"
                                className={`text-xs ${getStatusConfig(candidate.status).borderColor} ${getStatusConfig(candidate.status).color} ${getStatusConfig(candidate.status).bgColor}`}
                              >
                                {getLocalizedStatus(candidate.status)}
                              </Badge>
                            )}
                            {candidate.function && (
                              <Badge variant="secondary" className="text-xs">
                                <Briefcase className="h-3 w-3 mr-1" />
                                {candidate.function}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                          {candidate.email && (
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              <span className="truncate">{candidate.email}</span>
                            </div>
                          )}
                          {candidate.phone && (
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{candidate.phone}</span>
                            </div>
                          )}
                          {candidate.location && (
                            <div className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              <span className="truncate">{candidate.location}</span>
                            </div>
                          )}
                        </div>
                        {candidate.skills && candidate.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {candidate.skills.slice(0, 5).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 5}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-gray-600">
            {selectedCandidates.length > 0 && (
              <span>
                {t("lists.addCandidates.selected", "{{count}} selected", { count: selectedCandidates.length })}
              </span>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleAddCandidates}
              disabled={selectedCandidates.length === 0 || isLoading}
            >
              {isLoading 
                ? t("lists.addCandidates.adding", "Adding...")
                : t("lists.addCandidates.add", "Add {{count}} candidates", { count: selectedCandidates.length })
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
